/**
 * 2_AsdPADNextSteps.js — Next Steps (step 2 of the Adult Autism PAD)
 *
 * The closing step of the Post-Assessment Discussion, shown ONLY when the patient
 * Met diagnostic criteria (a Not-met assessment finishes on the Assessment
 * Outcome page). It records four yes/no decisions about the support areas to
 * include in the patient's onward plan:
 *
 *   • Relationship and Partner Support
 *   • Employment Support
 *   • Academic Support
 *   • Support for Autistic Women
 *
 * The support-area toggles are persisted only — there is no per-page LLM call;
 * they will drive conditional blocks in the future report template.
 *
 * Because this step is Met-only, the page redirects (replace) back to the
 * Assessment Outcome if the live DD outcome is anything other than Met (e.g. the
 * MDT flipped the decision after PAD was started).
 *
 * Finishing runs the shared useAutismPADCompletion flow, which completes the
 * stage and regenerates the "Assessment Outcome" report section (summary +
 * patient goals).
 */

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/solid';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PreviewModeBanner from '../../../components/PreviewModeBanner';
import ModularSideNavbar from '../../../components/autismModular/ModularSideNavbar';
import ModularStepFooter from '../../../components/autismModular/ModularStepFooter';
import AutismSupportingDocumentsPanel from '../../../components/autismReview/AutismSupportingDocumentsPanel';
import AutismDecisionCard from '../../../components/autismReview/AutismDecisionCard';
import DictationTextArea from '../../../components/DictationTextArea';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { useAutismPADCompletion } from '../../../hooks/useAutismPADCompletion';
import { getCombinedEmergingDifferentials } from '../../../utils/autismDDRouting';
import PatientContractTypeBadge from '../../../components/PatientContractTypeBadge';
import { asdLog } from '../../../utils/autismDebugLog';
import { auth } from '../../../firebase';
import {
  AUTISM_PAD_STAGE_KEY,
  AUTISM_PAD_DISPLAY_NAME,
  getAutismPADStepRoutes,
  getAutismDiagnosisOutcome,
  isNextStepsApplicable,
  isNextStepsComplete,
  computeAutismPADProgress,
  buildAutismPADNavSections,
  PAD_NEXT_STEPS_KEYS,
} from '../../../utils/autismPADRouting';

/** The four support-area decisions, in display order (keys match the data model). */
const NEXT_STEPS_ITEMS = [
  { key: 'relationshipAndPartnerSupport', label: 'Relationship and Partner Support', description: 'Include relationship and partner support guidance in the report.' },
  { key: 'employmentSupport', label: 'Employment Support', description: 'Include employment support guidance in the report.' },
  { key: 'academicSupport', label: 'Academic Support', description: 'Include academic support guidance in the report.' },
  { key: 'supportForAutisticWomen', label: 'Support for Autistic Women', description: 'Include support resources specific to autistic women in the report.' },
];

export default function AsdPADNextSteps() {
  const { patientId } = useParams();
  const navigate = usePreviewAwareNavigate();

  const {
    header,
    assessmentStages,
    updateAssessmentStage,
    flushStageWrites,
  } = useContext(ModularPatientDataContext);

  const isPreviewMode = useIsPreviewMode();
  const canEdit = useCanEditStage(header || {}, 'PAD', { previewMode: isPreviewMode });

  // Clinician's organisation gates the contract-type badge (only orgs that
  // categorise patients by contract type surface it). Value comes from the
  // patient header. Read-only, so it renders in preview mode too.
  const { claims } = useAuth();
  const wf = useWorkflowConfig(header, claims);

  // ── Workplace Adjustments gating ──────────────────────────────────────────
  // The Workplace Adjustments panel is a TOC-only feature (not shown for LMP
  // Adult Autism patients). Within TOC, the decision card is enabled only for
  // Lexxic contract patients; other contracts see it greyed out with a tooltip.
  const patientOrganisation = header?.organisation || '';
  const isTocOrganisation = patientOrganisation === 'The Owl Centre' || patientOrganisation === 'Demo Org';
  const contractType = header?.customFields?.assessmentType || '';
  const isWorkplaceAdjustmentsEnabled = isTocOrganisation && contractType === 'Lexxic Ltd';
  const isHampshireIowEnabled = isTocOrganisation && contractType === 'Hampshire & Isle of Wight Integrated Care Board';

  // ── ADHD Follow-Up gating ─────────────────────────────────────────────────
  // Mirrors the Autism Follow-Up panel on the ADHD PAD (6_PAD_TOC.js). When
  // differentials decisions are disabled (TOC), the old per-condition ADHD
  // referral logic in the DD module is no longer available, so the clinician
  // decides here whether the report should include ADHD follow-up instructions.
  // The panel is visible whenever the skip flag is true (TOC / Demo Org) but
  // only interactable when ADHD is actually an emerging differential. Uses the
  // same case-insensitive match as the backend _compute_adhd_referral_key.
  const isAdhdEmergingDifferential = useMemo(() => {
    const differentials = getCombinedEmergingDifferentials(assessmentStages);
    return differentials.some((name) => name.toLowerCase().includes('adhd'));
  }, [assessmentStages]);
  const showAdhdFollowUpPanel = wf.skipAutismDifferentialsDecisions;
  const isAdhdFollowUpActive = wf.skipAutismDifferentialsDecisions && isAdhdEmergingDifferential;

  const padStage = assessmentStages?.[AUTISM_PAD_STAGE_KEY];
  const savedNextSteps = padStage?.data?.nextSteps || {};

  const outcome = getAutismDiagnosisOutcome(assessmentStages);
  const isMet = isNextStepsApplicable(outcome);

  const routes = getAutismPADStepRoutes(patientId);

  // Lazy initial state — each toggle defaults to null (unanswered).
  // Includes the conditional workplaceAdjustments key (TOC + Lexxic only) and
  // hampshirePostDiagnosticReferral (Hampshire IOW only) so they persist
  // alongside the four core support-area toggles.
  const [nextSteps, setNextSteps] = useState(() => {
    const seed = {};
    for (const key of PAD_NEXT_STEPS_KEYS) {
      seed[key] = savedNextSteps[key] === true || savedNextSteps[key] === false
        ? savedNextSteps[key]
        : null;
    }
    seed.workplaceAdjustments = savedNextSteps.workplaceAdjustments === true || savedNextSteps.workplaceAdjustments === false
      ? savedNextSteps.workplaceAdjustments
      : null;
    seed.hampshirePostDiagnosticReferral = savedNextSteps.hampshirePostDiagnosticReferral === true || savedNextSteps.hampshirePostDiagnosticReferral === false
      ? savedNextSteps.hampshirePostDiagnosticReferral
      : null;
    // ADHD Follow-Up toggle — only relevant when differentials decisions are
    // disabled (TOC) and ADHD is an emerging differential. Persists alongside
    // the other contract-specific keys so the backend can read it.
    seed.includeAdhdFollowUp = savedNextSteps.includeAdhdFollowUp === true || savedNextSteps.includeAdhdFollowUp === false
      ? savedNextSteps.includeAdhdFollowUp
      : null;
    return seed;
  });

  // ── Workplace Adjustments declined rationale ────────────────────────────────
  // Separate state from the toggles to avoid Firestore writes on every keystroke.
  // Persisted on blur via its own updateAssessmentStage call (matching the ADHD
  // wnaDeclinedRationale pattern in 6_PAD_TOC.js).
  const [workplaceAdjustmentsDeclinedRationale, setWorkplaceAdjustmentsDeclinedRationale] = useState(
    () => savedNextSteps.workplaceAdjustmentsDeclinedRationale || ''
  );

  const handleRationaleBlur = () => {
    if (!canEdit) return;
    updateAssessmentStage(AUTISM_PAD_STAGE_KEY, {
      'data.nextSteps.workplaceAdjustmentsDeclinedRationale': workplaceAdjustmentsDeclinedRationale,
    });
  };

  // ── Redirect guard ──────────────────────────────────────────────────────────
  // This step is Met-only. Once stages have loaded, send the clinician back to
  // the Assessment Outcome if the outcome is not (or no longer) Met.
  useEffect(() => {
    if (!assessmentStages) return;
    if (!isMet) {
      // [ASD debug] This step is Met-only; trace the guard so an unexpected
      // bounce back to Assessment Outcome is easy to diagnose.
      asdLog('PAD', 'Next Steps redirect -> Outcome (not Met)', { outcome: outcome || 'undecided' });
      navigate(routes.assessmentOutcome, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentStages, isMet]);

  // ── Status promotion (notStarted -> inProgress on first write) ──────────────
  const hasStartedRef = useRef(false);
  const maybePromoteStatus = (updates) => {
    if (!hasStartedRef.current && padStage?.status === 'notStarted') {
      hasStartedRef.current = true;
      return { ...updates, status: 'inProgress' };
    }
    return updates;
  };

  // ── Single-writer persistence effect ───────────────────────────────────────
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!hydratedRef.current) { hydratedRef.current = true; return; }
    if (!canEdit) return;

    const nextData = { ...(padStage?.data || {}), nextSteps };
    let progress = computeAutismPADProgress({ data: nextData, outcome, status: padStage?.status });
    if (padStage?.status === 'completed' && progress < 1) progress = 1;

    updateAssessmentStage(AUTISM_PAD_STAGE_KEY, maybePromoteStatus({
      'data.nextSteps': nextSteps,
      progress,
    }));
    // [ASD debug] Trace the four support-area toggles + computed progress.
    asdLog('PAD', 'persist next steps', { nextSteps, progress });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, nextSteps]);

  const handleToggle = (key, value) => {
    if (!canEdit) return;
    setNextSteps(prev => ({ ...prev, [key]: prev[key] === value ? null : value }));
  };

  // All four core support-area toggles must be answered. Additionally, when
  // contract-specific panels are active, their decisions must also be answered
  // before finishing. If the clinician declines WNA (Lexxic), a rationale is
  // required (mirrors ADHD wnaDeclinedRationale gating).
  const coreComplete = isNextStepsComplete(nextSteps);
  const workplaceRequired = isWorkplaceAdjustmentsEnabled && (nextSteps.workplaceAdjustments === null);
  const rationaleRequired = isWorkplaceAdjustmentsEnabled
    && nextSteps.workplaceAdjustments === false
    && !workplaceAdjustmentsDeclinedRationale.trim();
  const hampshireRequired = isHampshireIowEnabled && (nextSteps.hampshirePostDiagnosticReferral === null);
  // ADHD Follow-Up is required only when the panel is active (differentials
  // decisions disabled AND ADHD is an emerging differential for this patient).
  const adhdFollowUpRequired = isAdhdFollowUpActive && (nextSteps.includeAdhdFollowUp === null);
  const allAnswered = coreComplete && !workplaceRequired && !rationaleRequired && !hampshireRequired && !adhdFollowUpRequired;

  // Whether there is meaningful LLM work to trigger for the Workplace
  // Adjustments section: only when a Lexxic patient has declined AND typed
  // a rationale. Passed to handleComplete as an additional section to generate.
  const hasWnaToGenerate = isWorkplaceAdjustmentsEnabled
    && nextSteps.workplaceAdjustments === false
    && !!workplaceAdjustmentsDeclinedRationale.trim();

  // ── Completion ──────────────────────────────────────────────────────────────
  // padStage lets the hook stamp firstCompletedAt write-once; the appointment
  // flag gates the inferred-appointment audit entry (see the hook's docs).
  const padCompletion = useAutismPADCompletion({
    patientId, updateAssessmentStage, flushStageWrites, canEdit,
    padStage, isAppointmentDatesEnabled: wf.isAppointmentDatesEnabled,
  });

  const { items: navItems } = useMemo(
    () => buildAutismPADNavSections({ assessmentStages, currentSectionIndex: 1 }),
    [assessmentStages],
  );

  const navigateToSection = async (id) => {
    if (id === 'nextSteps') return;
    await flushStageWrites(AUTISM_PAD_STAGE_KEY);
    navigate(routes.assessmentOutcome);
  };

  const handleBack = async () => {
    await flushStageWrites(AUTISM_PAD_STAGE_KEY);
    navigate(routes.assessmentOutcome);
  };

  const handleAfterGeneration = () => {
    navigate(`/patient/${patientId}/adult-autism/documents`);
  };

  // Finish wrapper: forces a blur (to persist any in-flight rationale text)
  // and passes additional sections to generate when the WNA declined paragraph
  // is needed. The blur fires handleRationaleBlur synchronously so the text is
  // included in the stage write that flushStageWrites will commit.
  const handleFinish = async () => {
    document.activeElement?.blur();
    const sections = hasWnaToGenerate ? ['Workplace Adjustments'] : [];
    await padCompletion.handleComplete(sections);
  };

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
    ['Adult Autism Assessment'],
    // Module name links back to the module's landing page (Assessment Outcome),
    // mirroring how the DD sub-pages link "Diagnostic Decision".
    [AUTISM_PAD_DISPLAY_NAME, getAutismPADStepRoutes(patientId).assessmentOutcome],
    ['Next Steps'],
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      {/* Wizard shell — shared sidebar + scrollable content + fixed footer. */}
      <div className="flex h-[calc(100vh-8rem)]">
        <ModularSideNavbar
          items={navItems}
          activeId="nextSteps"
          onSelect={navigateToSection}
        />

        <div className="flex-1 flex flex-col min-h-0">
          {/* Preview mode info banner — shown when opened via a Preview button */}
          {isPreviewMode && <PreviewModeBanner />}
          {/* Body — cloned from the ADHD PAD "Next Steps" page
              (9_postAssessmentDiscussion/6_PAD_TOC.js): a left-aligned numbered
              section title, the Supporting Documents panel, then a full-width
              "p-8 my-8" panel of Yes/No DecisionCards. Only the data plumbing
              (modular context) and the brand colour (primary, not violet) differ
              from the ADHD original. */}
          <div className="flex-1 overflow-y-auto p-8 text-left">
            {/* Heading left, contract-type badge right-aligned via justify-between. */}
            <div className="flex justify-between items-center gap-3 mb-6">
              <h2 className="text-2xl font-bold">2. Next Steps</h2>
              <PatientContractTypeBadge
                label="Contract"
                organisation={claims?.organisation}
                assessmentType={header?.customFields?.assessmentType}
                subContractType={header?.customFields?.subContractType}
              />
            </div>

            {/* Supporting Documents — live previews of the rendered upstream
                Autism reports (History/SSI). Hidden until at least one has a
                rendered PDF. Mirrors the ADHD PAD layout. */}
            <AutismSupportingDocumentsPanel
              patientId={patientId}
              clinicianId={auth.currentUser?.uid}
            />

            {/* 2.1 Support Areas — one ADHD-style Yes/No DecisionCard per
                support area. */}
            <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
              <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                2.1 Support Areas
              </h3>
              <p className="text-left text-gray-700 mb-6">
                For each area, decide whether it should be included in the patient&apos;s onward plan.
              </p>

              <div className="space-y-4">
                {NEXT_STEPS_ITEMS.map(({ key, label, description }) => (
                  <AutismDecisionCard
                    key={key}
                    question={label}
                    description={description}
                    value={nextSteps[key]}
                    onChange={(val) => handleToggle(key, val)}
                    disabled={!canEdit}
                  />
                ))}
              </div>
            </div>

            {/* 2.2 Workplace Adjustments — TOC-only panel (hidden for LMP).
                Within TOC, the decision is only actionable for Lexxic contract
                patients; other TOC contracts see the panel greyed out with a
                tooltip explaining why. Mirrors the ADHD PAD "Workplace Needs
                Assessment" panel in 6_PAD_TOC.js. */}
            {isTocOrganisation && (
              <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
                <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                  2.2 Workplace Adjustments
                </h3>
                <div className="space-y-4">
                  <AutismDecisionCard
                    question="Would patient benefit from workplace adjustments?"
                    description="Include workplace adjustments guidance in the report."
                    value={isWorkplaceAdjustmentsEnabled ? nextSteps.workplaceAdjustments : null}
                    onChange={(val) => handleToggle('workplaceAdjustments', val)}
                    disabled={!canEdit || !isWorkplaceAdjustmentsEnabled}
                    inactive={!isWorkplaceAdjustmentsEnabled}
                    tooltip={!isWorkplaceAdjustmentsEnabled ? 'Only applicable for Lexxic contract patients.' : undefined}
                  />

                  {/* Animated rationale textbox — appears when the clinician
                      selects "No" for a Lexxic patient. The text is used as
                      LLM input to generate a patient-facing paragraph explaining
                      why workplace adjustments were not recommended. */}
                  <div
                    className="overflow-hidden transition-all duration-500 ease-in-out"
                    style={{
                      maxHeight: (isWorkplaceAdjustmentsEnabled && nextSteps.workplaceAdjustments === false) ? '300px' : '0px',
                      opacity: (isWorkplaceAdjustmentsEnabled && nextSteps.workplaceAdjustments === false) ? 1 : 0,
                    }}
                  >
                    <div className="mt-4 text-left">
                      <h4 className="text-gray-700 text-sm font-semibold mb-2">
                        Please explain why workplace adjustments are not recommended for this patient
                        <span className="text-red-600 ml-1">*</span>
                      </h4>
                      <DictationTextArea
                        value={workplaceAdjustmentsDeclinedRationale}
                        onChange={(text) => setWorkplaceAdjustmentsDeclinedRationale(text)}
                        onBlur={handleRationaleBlur}
                        canEdit={canEdit}
                        placeholder="e.g. The patient is not currently employed or seeking employment, therefore workplace adjustments would not be beneficial at this time..."
                        ariaLabel="Rationale for declining workplace adjustments"
                        rows={4}
                        minHeight="8rem"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 2.3 ADHD Follow-Up — shown when differentials decisions are
                disabled (TOC / Demo Org). The panel is always visible for these
                orgs but only interactable when ADHD is an emerging differential
                from the SSI or Observation reviews. Mirrors the Autism Follow-Up
                panel on the ADHD PAD (6_PAD_TOC.js). */}
            {showAdhdFollowUpPanel && (
              <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
                <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                  2.3 ADHD Follow-Up
                </h3>
                <div className="space-y-4">
                  <AutismDecisionCard
                    question="Include ADHD follow-up instructions in the report?"
                    value={isAdhdFollowUpActive ? nextSteps.includeAdhdFollowUp : null}
                    onChange={(val) => handleToggle('includeAdhdFollowUp', val)}
                    disabled={!canEdit || !isAdhdFollowUpActive}
                    inactive={!isAdhdFollowUpActive}
                    tooltip={!isAdhdFollowUpActive
                      ? 'This only applies when ADHD is selected as an emerging differential.'
                      : 'When set to Yes, the report will include a boilerplate paragraph advising the patient to seek a follow-up ADHD assessment.'}
                  />
                </div>
              </div>
            )}

            {/* 2.4 Post-Diagnostic Service (Hampshire IOW) — TOC-only panel,
                enabled only for Hampshire IOW contract patients. A simple Yes/No
                referral decision with static report text for each outcome (no
                LLM generation needed). */}
            {isTocOrganisation && (
              <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
                <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                  {showAdhdFollowUpPanel ? '2.4' : '2.3'} Post-Diagnostic Service (Hampshire IOW)
                </h3>
                <div className="space-y-4">
                  <AutismDecisionCard
                    question="Patient would like to be referred to post-diagnostic service provided by Autism Hampshire?"
                    description="Record whether the patient consents to referral to Autism Hampshire post-diagnostic service."
                    value={isHampshireIowEnabled ? nextSteps.hampshirePostDiagnosticReferral : null}
                    onChange={(val) => handleToggle('hampshirePostDiagnosticReferral', val)}
                    disabled={!canEdit || !isHampshireIowEnabled}
                    inactive={!isHampshireIowEnabled}
                    tooltip={!isHampshireIowEnabled ? 'Only applicable for Hampshire IOW patients.' : undefined}
                  />

                  {/* Clinician action reminder — same max-height/opacity reveal as
                      the RSK risk banner (11_RSK.js). Shown only when Hampshire
                      IOW is Yes; the report itself already records the referral
                      consent, this is the off-platform follow-up checklist. */}
                  <div
                    className={`overflow-hidden transition-all duration-400 ease-in-out ${
                      isHampshireIowEnabled && nextSteps.hampshirePostDiagnosticReferral === true
                        ? 'max-h-96 mt-4 opacity-100'
                        : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="bg-cyan-50 rounded-2xl p-4 text-left">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mt-1">
                          <InformationCircleIcon className="h-5 w-5 text-cyan-700" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                          <h4 className="text-md font-medium text-cyan-900">
                            If patient says they want this referral, as the feedback clinician you should:
                          </h4>
                          <ul className="mt-2 text-sm text-cyan-900 space-y-1 list-disc list-inside">
                            <li>Open the referral form saved in MSTeams Adult PDG referral form.docx</li>
                            <li>Complete the referral form and upload to Nest files</li>
                            <li>
                              Email Adeebah,{' '}
                              <a
                                href="mailto:adeebahali@theowltherapycentre.co.uk"
                                className="underline"
                              >
                                adeebahali@theowltherapycentre.co.uk
                              </a>
                              {' '}with the patient name saying they want to be added to post diagnostic referral list
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <ModularStepFooter
            showBack
            onBack={handleBack}
            backLabel="Back"
            counterText={`2 of ${navItems.length}`}
            rightActions={canEdit ? (
              <button
                onClick={handleFinish}
                disabled={!allAnswered || padCompletion.isSaving}
                title={!allAnswered ? 'Answer all required questions before finishing.' : undefined}
                className={`flex items-center gap-2 py-2 px-4 rounded-md shadow-sm transition-all duration-200 text-sm font-semibold whitespace-nowrap ${
                  !allAnswered || padCompletion.isSaving
                    ? padCompletion.isSaving
                      ? 'opacity-60 cursor-not-allowed bg-primary text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-primary text-white hover:bg-primary/90'
                }`}
              >
                {padCompletion.isSaving ? 'Saving...' : 'Finish Feedback Session'}
                {padCompletion.isSaving
                  ? <ButtonSpinner className="w-5 h-5" />
                  : <CheckCircleIcon className="w-5 h-5" aria-hidden="true" />}
              </button>
            ) : undefined}
          />
        </div>
      </div>

      <GenerateReportProgressDialog
        // Display-only label for the simulated progress dialog — real LLM
        // generation uses AUTISM_PAD_REPORT_STAGE ("Autism Post-Assessment Discussion").
        reportStage={AUTISM_PAD_DISPLAY_NAME}
        patientId={patientId}
        clinicianId={auth.currentUser?.uid}
        handleNextPageButtonClick={handleAfterGeneration}
        isGenerating={padCompletion.isGenerating}
        onCancel={() => padCompletion.setIsGenerating(false)}
        completionButtonLabel="Go to Documents"
      />
    </Layout>
  );
}
